import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import NavbarAr from "@/components/NavbarAr";
import Footer from "@/components/Footer";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";

// Account Deletion — bilingual (EN / AR) instructions and request
// page. This page is instructional only. The authoritative
// destructive delete flow lives inside the CrewZNatioN mobile
// application, behind authentication. This public route exists to
// satisfy the App Store / Google Play requirement of a public,
// unauthenticated URL that explains how a user can delete their
// account and what happens when they do.
//
// Content is aligned to the CrewZNatioN Privacy Policy and does not
// invent legal deadlines, absolute deletion promises, or vendor
// names. Retention exceptions match Privacy §12.

const CONTROLLER_EN =
    "Masirat Al-Hayat for Entertainment (مسيرة الحياة للترفيه), operator of CrewZNatioN";
const CONTROLLER_AR = "مسيرة الحياة للترفيه، المُشغِّل لعلامة CrewZNatioN";
const PRIVACY_EMAIL = "admin@crewznation.com";
const EFFECTIVE_EN = "8 August 2026";
const EFFECTIVE_AR = "٨ أغسطس ٢٠٢٦";

const SECTIONS = [
    {
        id: "overview",
        en: {
            heading: "1. Overview",
            body: [
                <>
                    This page explains how to permanently delete a <Brand /> account and what data is affected by that request. It applies to accounts created inside the CrewZNatioN mobile application on iOS and Android, and to any web-side records associated with that account.
                </>,
                <>
                    Two paths are available: an in-app self-service path (recommended), and a written request sent to <Brand /> support. Both paths lead to the same outcome and are governed by the same retention rules described below.
                </>,
                <>
                    A public, unauthenticated deletion endpoint is intentionally not offered. Deletion must be initiated either from a signed-in device or through a verifiable written request, so that no third party can close an account without the account holder&apos;s consent.
                </>,
            ],
        },
        ar: {
            heading: "١. نظرة عامة",
            body: [
                <>
                    توضّح هذه الصفحة كيفية حذف حساب <Brand /> بشكلٍ نهائي، والبيانات التي يشملها الطلب. وتنطبق على الحسابات التي أُنشئت داخل تطبيق CrewZNatioN للأجهزة الجوّالة على نظامَي iOS و Android، وعلى أي سجلاتٍ مرتبطةٍ بها على الويب.
                </>,
                <>
                    يتوفّر مساران: مسار الخدمة الذاتية داخل التطبيق (المُوصى به)، ومسار الطلب الكتابي المُرسَل إلى دعم <Brand />. ويُفضي كلا المسارين إلى النتيجة نفسها، ويخضعان لقواعد الاحتفاظ الموضّحة أدناه.
                </>,
                <>
                    ولا تُتاح عن قصدٍ نقطة نهاية عامة لحذف الحساب دون تسجيل دخول. يجب أن يبدأ الحذف إما من جهازٍ مسجَّل الدخول، أو من خلال طلبٍ كتابيٍّ يمكن التحقّق منه، حتى لا يتمكّن طرفٌ ثالث من إغلاق الحساب دون موافقة صاحبه.
                </>,
            ],
        },
    },
    {
        id: "in-app",
        en: {
            heading: "2. In-app deletion (recommended path)",
            body: [
                <>The in-app path is the fastest and most secure way to close a <Brand /> account. It runs entirely from a signed-in session on the device that owns the account.</>,
            ],
            list: [
                <>Open the CrewZNatioN app and sign in to the account that is to be deleted.</>,
                <>Open the profile / settings area of the app.</>,
                <>Locate <em>Account &amp; privacy</em> (or the equivalent settings section shown in the current app build) and choose <em>Delete account</em>.</>,
                <>Confirm the request when prompted. The app may require re-entry of the account password, or a one-time verification code sent to the registered email or mobile number, to confirm that the request is genuine.</>,
                <>Once confirmed, the account is <strong>scheduled for deletion</strong>. It is disabled and hidden from other members from that moment. The account is <strong>not</strong> permanently deleted immediately — a <strong>30-day cancellation window</strong> begins.</>,
                <>During those 30 days the member can sign back in from a trusted device and <strong>cancel the deletion</strong> to restore the account. After day 30 the cancellation window closes, destructive processing begins, and the account and its associated personal data are permanently deleted or irreversibly anonymized (subject to the retention exceptions in section 5).</>,
            ],
            footer: [
                <>If the in-app option is unavailable — for example, because the account is locked, the sign-in factor was lost, or the app version is outdated — use the written request path in section 3.</>,
            ],
        },
        ar: {
            heading: "٢. الحذف من داخل التطبيق (المسار المُوصى به)",
            body: [
                <>يُعدّ الحذف من داخل التطبيق أسرع الطرق وأكثرها أمانًا لإغلاق حساب <Brand />. ويجري بالكامل من جلسةٍ مسجَّلة الدخول على الجهاز الذي يملك الحساب.</>,
            ],
            list: [
                <>افتح تطبيق CrewZNatioN وسجّل الدخول إلى الحساب المراد حذفه.</>,
                <>ادخل إلى قسم الملف الشخصي/الإعدادات في التطبيق.</>,
                <>افتح قسم <em>الحساب والخصوصية</em> (أو ما يقابله في إصدار التطبيق الحالي)، ثم اختر <em>حذف الحساب</em>.</>,
                <>أكّد الطلب عند طلب ذلك. قد يطلب التطبيق إعادة إدخال كلمة المرور، أو رمز تحقّقٍ يُرسَل مرةً واحدة إلى البريد الإلكتروني أو رقم الجوّال المسجَّلَين، للتأكّد من صحة الطلب.</>,
                <>عند التأكيد، يُصبح الحساب <strong>مُجدوَلًا للحذف</strong>. ويُعطَّل ويُخفى عن بقية الأعضاء من تلك اللحظة. ولا يُحذَف الحساب <strong>نهائيًّا</strong> فورًا؛ بل تبدأ <strong>مهلة إلغاءٍ مدّتها ٣٠ يومًا</strong>.</>,
                <>خلال هذه الأيام الثلاثين يمكن للعضو تسجيل الدخول من جهازٍ موثوق و<strong>إلغاء الحذف</strong> لاسترجاع الحساب. وبعد اليوم الثلاثين تُغلَق مهلة الإلغاء، وتبدأ عملية الحذف الفعلية، ويُحذَف الحساب وبياناته الشخصية المرتبطة به بشكلٍ نهائي أو يُجهَّل بشكلٍ لا يمكن عكسه (وفق استثناءات الاحتفاظ الواردة في الفقرة ٥).</>,
            ],
            footer: [
                <>وإذا لم يتوفّر هذا الخيار داخل التطبيق — على سبيل المثال بسبب قفل الحساب، أو فقدان وسيلة تسجيل الدخول، أو قِدَم إصدار التطبيق — يمكن استخدام مسار الطلب الكتابي في الفقرة ٣.</>,
            ],
        },
    },
    {
        id: "written",
        en: {
            heading: "3. Written deletion request",
            body: [
                <>
                    A deletion request may be sent by email to <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${PRIVACY_EMAIL}?subject=Account%20Deletion%20Request`}>{PRIVACY_EMAIL}</a> with the subject line <em>Account Deletion Request</em>.
                </>,
                <>To help <Brand /> act on the request, the message should include, from the same email address that owns the account:</>,
            ],
            list: [
                <>the username and/or the email address registered on the account;</>,
                <>a clear statement that the account holder is requesting deletion (deactivation is a separate, reversible option — see section 6);</>,
                <>optionally, the reason for closing the account.</>,
            ],
            footer: [
                <>
                    <Brand /> may reply to the same email address to verify identity with the minimum information necessary. <strong>Passwords, verification codes, full payment-card numbers, national identification numbers or other highly sensitive documents must never be sent to support by email.</strong> A request that arrives with such information will be handled and the sensitive content will be removed from support records.
                </>,
            ],
        },
        ar: {
            heading: "٣. الطلب الكتابي لحذف الحساب",
            body: [
                <>
                    يمكن إرسال طلب الحذف عبر البريد الإلكتروني إلى <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${PRIVACY_EMAIL}?subject=%D8%B7%D9%84%D8%A8%20%D8%AD%D8%B0%D9%81%20%D8%AD%D8%B3%D8%A7%D8%A8`}>{PRIVACY_EMAIL}</a> مع كتابة العنوان <em>طلب حذف حساب</em>.
                </>,
                <>وحتى تتمكّن <Brand /> من معالجة الطلب، يُفضَّل أن تتضمّن الرسالة — مُرسَلةً من البريد الإلكتروني نفسه المرتبط بالحساب — ما يلي:</>,
            ],
            list: [
                <>اسم المستخدم و/أو البريد الإلكتروني المسجَّل على الحساب؛</>,
                <>بيان صريح بأن صاحب الحساب يطلب الحذف (يُعدّ إلغاء التنشيط خيارًا منفصلًا وقابلًا للاسترجاع — يُنظر إلى الفقرة ٦)؛</>,
                <>اختياريًّا، سبب إغلاق الحساب.</>,
            ],
            footer: [
                <>
                    قد تردّ <Brand /> على البريد نفسه للتحقّق من الهوية بالحدّ الأدنى من المعلومات اللازمة. <strong>ولا يجوز مطلقًا إرسال كلمات المرور، أو رموز التحقّق، أو أرقام بطاقات الدفع الكاملة، أو أرقام الهوية الوطنية، أو أي مستنداتٍ حسّاسةٍ عبر البريد الإلكتروني إلى الدعم.</strong> وإذا وصل طلبٌ يحتوي على مثل هذه البيانات، سيُعالَج ويُحذَف المحتوى الحسّاس من سجلّات الدعم.
                </>,
            ],
        },
    },
    {
        id: "verification",
        en: {
            heading: "4. Identity verification",
            body: [
                <>Before acting on any deletion request, <Brand /> confirms that the requester is the legitimate account holder. Verification is proportionate and uses only the minimum information needed. Typical checks include one or more of the following:</>,
            ],
            list: [
                <>replying from the email address registered on the account;</>,
                <>confirming account details that a stranger would not know (approximate registration date, associated username, general account activity);</>,
                <>completing a one-time verification code that <Brand /> sends to the email or mobile number on file;</>,
                <>demonstrating ownership from a signed-in device in the app.</>,
            ],
            footer: [
                <>Government-issued identity documents are not requested by default. If a particular case requires them (for example, a disputed account or a legal representative acting for another person), <Brand /> will explain why and only accept the specific document required.</>,
            ],
        },
        ar: {
            heading: "٤. التحقّق من الهوية",
            body: [
                <>قبل تنفيذ أي طلب حذف، تتأكّد <Brand /> من أن مقدّم الطلب هو صاحب الحساب. ويكون التحقّق متناسبًا، ولا يستخدم إلا الحدّ الأدنى من المعلومات. وتشمل الوسائل المعتادة واحدةً أو أكثر مما يلي:</>,
            ],
            list: [
                <>الردّ من البريد الإلكتروني المسجَّل على الحساب؛</>,
                <>تأكيد تفاصيلٍ لا يعرفها إلا صاحب الحساب (كتاريخ التسجيل التقريبي، أو اسم المستخدم، أو ملخّص نشاط الحساب)؛</>,
                <>إدخال رمز تحقّقٍ لمرةٍ واحدة تُرسله <Brand /> إلى البريد أو رقم الجوّال المسجَّل؛</>,
                <>إثبات الملكية من جهازٍ مسجَّل الدخول داخل التطبيق.</>,
            ],
            footer: [
                <>لا تُطلَب مستندات الهوية الحكومية بشكلٍ اعتيادي. وإذا استدعت حالةٌ معيّنة ذلك (كنزاعٍ على حساب، أو ممثّلٍ قانونيٍّ ينوب عن شخصٍ آخر)، ستوضّح <Brand /> السبب، ولن تقبل إلا الوثيقة المحدّدة اللازمة.</>,
            ],
        },
    },
    {
        id: "scope",
        en: {
            heading: "5. What is deleted, anonymized, or retained",
            body: [
                <>When a deletion is confirmed, the account enters a <strong>30-day cancellation window</strong>. During this window the account is disabled and hidden from other members but is not yet permanently deleted, so a member who changes their mind can restore it. When the window closes on day 30, <Brand /> removes or irreversibly anonymizes the active-service personal data linked to the account. Some records must be retained for legal, security, safety or accounting reasons, and are described here for full transparency.</>,
            ],
            list: [
                <><strong>Hidden from other members immediately when deletion is scheduled (day 0):</strong> profile, avatar, bio, Garage listings and vehicle uploads, Feed posts, comments, uploaded photos/videos/GIFs, reactions, MY CREW relationships, RSVP records, and Marketplace listings. Nothing new is shown from the account during the 30-day cancellation window.</>,
                <><strong>Reversible during the 30-day cancellation window:</strong> the member may sign back in and cancel the deletion. On cancellation the account and its hidden content are restored to the live service.</>,
                <><strong>Permanently deleted or irreversibly anonymized on day 30, when the cancellation window closes:</strong> profile, avatar, bio, Garage listings, vehicle uploads, Feed posts, comments, uploaded photos/videos/GIFs, reactions, MY CREW relationships, RSVP records, Marketplace listings, plus the account identifiers, email, mobile number, date of birth and consent records held in active systems — subject to the exceptions below.</>,
                <><strong>Retained temporarily inside encrypted backups after day 30:</strong> residual copies may persist for up to <strong>90 days</strong> before automatic overwrite, and are not restored to the live service.</>,
                <><strong>Retained for defined periods where legally or operationally necessary:</strong> event QR/pass, scan and check-in records for up to <strong>24 months</strong> after the relevant event; safety, moderation, security and fraud-prevention case files for up to <strong>3 years</strong> after case closure; consent, withdrawal and internal processing records for the period necessary to demonstrate compliance (internal records of processing activities: up to <strong>five years</strong> after the activity ends).</>,
                <><strong>Kept indefinitely only after irreversible anonymization:</strong> aggregated or de-identified statistical records that no longer identify the account holder.</>,
                <><strong>Content posted by other members:</strong> comments, reports, or references made about the account by other users are the responsibility of those users. Content that mentions the account may remain on other users&apos; posts unless it is removed by that user or by moderation.</>,
                <><strong>Pending items:</strong> if the account has an open Marketplace listing, an active safety report, an event with QR/pass records, or an outstanding administrative matter, that item is closed or resolved as part of the deletion; the underlying operational record is retained for the period described above.</>,
            ],
        },
        ar: {
            heading: "٥. ما يُحذَف، وما يُجهَّل، وما يُحتفَظ به",
            body: [
                <>عند تأكيد الحذف، يدخل الحساب في <strong>مهلة إلغاءٍ مدّتها ٣٠ يومًا</strong>. وخلال هذه المهلة يُعطَّل الحساب ويُخفى عن بقية الأعضاء، لكنّه لا يُحذَف نهائيًّا بعد، حتى يتمكّن العضو من استرجاعه إن غيّر رأيه. وحين تنقضي المهلة في اليوم الثلاثين، تُزيل <Brand /> بيانات الخدمة الشخصية النشِطة المرتبطة بالحساب أو تُجهِّلها بشكلٍ لا يمكن عكسه. غير أنه يجب الاحتفاظ ببعض السجلات لأسبابٍ نظاميةٍ أو أمنيةٍ أو محاسبيةٍ أو متعلّقةٍ بالسلامة، وتُذكَر هنا لأغراض الشفافية.</>,
            ],
            list: [
                <><strong>يُخفى عن بقية الأعضاء فور جدولة الحذف (اليوم صفر):</strong> الملف الشخصي، والصورة الرمزية، والنبذة، وقوائم الجراج ورفوعات المركبات، ومنشورات التغذية، والتعليقات، والصور/مقاطع الفيديو/ملفات GIF المرفوعة، والتفاعلات، وعلاقات &quot;طاقمي&quot;، وسجلّات تأكيد الحضور، وإعلانات السوق. ولا يُنشَر أي محتوى جديد من الحساب خلال مهلة الإلغاء البالغة ٣٠ يومًا.</>,
                <><strong>قابل للاسترجاع خلال مهلة الإلغاء البالغة ٣٠ يومًا:</strong> يمكن للعضو تسجيل الدخول وإلغاء الحذف؛ فيُستعاد الحساب ومحتواه المخفي إلى الخدمة الحيّة.</>,
                <><strong>يُحذَف نهائيًّا أو يُجهَّل بشكلٍ لا يمكن عكسه في اليوم الثلاثين، عند انقضاء مهلة الإلغاء:</strong> الملف الشخصي، والصورة الرمزية، والنبذة، وقوائم الجراج ورفوعات المركبات، ومنشورات التغذية، والتعليقات، والصور/الفيديو/ملفات GIF المرفوعة، والتفاعلات، وعلاقات &quot;طاقمي&quot;، وسجلّات تأكيد الحضور، وإعلانات السوق، إضافةً إلى معرِّفات الحساب والبريد الإلكتروني ورقم الجوّال وتاريخ الميلاد وسجلّات الموافقات في الأنظمة النشِطة — وفق الاستثناءات أدناه.</>,
                <><strong>يُحتفَظ به مؤقتًا داخل النسخ الاحتياطية المشفَّرة بعد اليوم الثلاثين:</strong> قد تبقى نسخٌ متبقّية حتى <strong>٩٠ يومًا</strong> قبل استبدالها تلقائيًّا، ولا تُعاد إلى الخدمة الحيّة.</>,
                <><strong>يُحتفَظ به لمددٍ محدَّدة عند الحاجة النظامية أو التشغيلية:</strong> سجلّات QR/التصاريح ومسح الحضور للفعاليات حتى <strong>٢٤ شهرًا</strong> بعد الفعالية؛ ملفّات السلامة، والرقابة على المحتوى، والأمن، ومكافحة الاحتيال حتى <strong>٣ سنوات</strong> بعد إغلاق الحالة؛ سجلّات الموافقات وسحبها والسجلّات الداخلية للمعالجة للمدة اللازمة لإثبات الامتثال (السجلّات الداخلية لأنشطة المعالجة: حتى <strong>خمس سنوات</strong> بعد انتهاء النشاط).</>,
                <><strong>يُحتفَظ به دون مدةٍ ثابتة فقط بعد التجهيل الذي لا يمكن عكسه:</strong> السجلّات الإحصائية المجمَّعة أو غير القابلة للتعريف بصاحبها.</>,
                <><strong>محتوى نشره أعضاء آخرون:</strong> التعليقات أو البلاغات أو الإشارات التي يذكرها أعضاء آخرون عن الحساب تبقى على مسؤولية أصحابها. وقد يبقى المحتوى الذي يذكر الحساب على منشورات مستخدمين آخرين ما لم يقم هؤلاء أنفسهم بإزالته، أو تتدخّل الرقابة.</>,
                <><strong>البنود المعلَّقة:</strong> إذا كان للحساب إعلانٌ مفتوح في السوق، أو بلاغ سلامة نشِط، أو فعاليةٌ تتضمّن سجلّات QR، أو مسألةٌ إداريةٌ قائمة، تُغلَق هذه البنود أو تُحسَم ضمن عملية الحذف، ويُحتفَظ بالسجلّ التشغيلي المرتبط بها للمدد الموضّحة أعلاه.</>,
            ],
        },
    },
    {
        id: "deactivate",
        en: {
            heading: "6. Deactivation vs. scheduled deletion",
            body: [
                <>
                    Deactivation and deletion are two different actions, chosen explicitly by the member.
                </>,
                <>
                    <strong>Deactivation</strong> is reversible <em>indefinitely</em>. It hides the account from public view and pauses notifications while preserving the account so the member can return whenever they choose. There is no clock and no automatic deletion at the end of a deactivation.
                </>,
                <>
                    <strong>Scheduled deletion</strong> is the process described on this page. It is reversible only <em>during the 30-day cancellation window</em>. After day 30 it becomes permanent and cannot be undone; the member would need to register a new account to return to the service.
                </>,
                <>
                    If a member is unsure, deactivation from inside the app is often the safer first step. Neither action is selected by <Brand /> on the member&apos;s behalf.
                </>,
            ],
        },
        ar: {
            heading: "٦. إلغاء التنشيط مقابل الحذف المُجدوَل",
            body: [
                <>
                    إلغاء التنشيط والحذف إجراءان مختلفان، ويختارهما العضو صراحةً.
                </>,
                <>
                    <strong>إلغاء التنشيط</strong> قابلٌ للاسترجاع <em>دون مدةٍ محدَّدة</em>؛ يُخفي الحساب من العرض العام ويوقف الإشعارات مع الاحتفاظ بالحساب ليعود إليه العضو متى شاء. لا يوجد عدّاد زمني، ولا يتحوّل إلى حذفٍ تلقائيٍّ في نهايته.
                </>,
                <>
                    <strong>الحذف المُجدوَل</strong> هو ما توضّحه هذه الصفحة، وهو قابلٌ للاسترجاع <em>فقط خلال مهلة الإلغاء البالغة ٣٠ يومًا</em>. وبعد اليوم الثلاثين يُصبح نهائيًّا ولا يمكن التراجع عنه، ويتعيّن على العضو إنشاء حسابٍ جديد للعودة إلى الخدمة.
                </>,
                <>
                    وإذا لم يكن العضو متأكّدًا، فإن إلغاء التنشيط من داخل التطبيق كثيرًا ما يكون الخطوة الأولى الأكثر أمانًا. ولا تختار <Brand /> نيابةً عن العضو في أيٍّ من الحالتين.
                </>,
            ],
        },
    },
    {
        id: "timing",
        en: {
            heading: "7. Processing expectations",
            body: [
                <>
                    Once identity is verified, the account enters the <strong>30-day cancellation window</strong> and is disabled and hidden immediately. On day 30 the cancellation window closes and destructive processing begins; removal of the account&rsquo;s active-service data typically completes shortly thereafter, in line with the retention schedule in the Privacy Policy. Residual copies inside encrypted backups may persist for up to a further <strong>90 days</strong> before automatic overwrite, and are not restored to the live service.
                </>,
                <>
                    Where the account has open items — for example, an active safety report or a pending Marketplace listing — closure of those items may extend the processing that follows the 30-day window; the requester will be told when that applies. <Brand /> does not claim a shorter, fixed statutory deadline than what Saudi law and the Privacy Policy provide.
                </>,
            ],
        },
        ar: {
            heading: "٧. توقّعات المعالجة",
            body: [
                <>
                    بعد التحقّق من الهوية، يدخل الحساب في <strong>مهلة الإلغاء البالغة ٣٠ يومًا</strong>، ويُعطَّل ويُخفى فورًا. وفي اليوم الثلاثين تُغلَق مهلة الإلغاء وتبدأ عملية الحذف الفعلية، وتكتمل عادةً إزالة بيانات الحساب من الأنظمة النشِطة بعد ذلك بفترةٍ قصيرة، بما يتّسق مع جدول الاحتفاظ الوارد في سياسة الخصوصية. وقد تبقى نسخٌ متبقّية داخل النسخ الاحتياطية المشفَّرة حتى <strong>٩٠ يومًا</strong> إضافيةً قبل الاستبدال التلقائي، ولا تُعاد إلى الخدمة الحيّة.
                </>,
                <>
                    وحين يكون للحساب بنودٌ مفتوحة — كبلاغ سلامة قائم، أو إعلانٍ معلَّق في السوق — قد يتطلّب إغلاقها وقتًا أطول ضمن معالجة ما بعد مهلة الثلاثين يومًا، ويُبلَّغ صاحب الطلب متى انطبق ذلك. ولا تدّعي <Brand /> مدةً نظاميةً محدّدةً أقصر مما يقرّره النظام السعودي وسياسة الخصوصية.
                </>,
            ],
        },
    },
    {
        id: "changed-mind",
        en: {
            heading: "8. Cancelling a scheduled deletion",
            body: [
                <>
                    A scheduled deletion can be cancelled by the account holder at any point during the <strong>30-day cancellation window</strong>. The cancellation is initiated by signing back in to the CrewZNatioN app from a trusted device and confirming the cancel action; the account and the content that was hidden on day 0 are then restored to the live service.
                </>,
                <>
                    If the deletion was requested by email, the member may also reply to the same support thread within the 30-day window and ask <Brand /> to cancel. <Brand /> will verify the request using the same identity checks described in section 4 before restoring the account.
                </>,
                <>
                    Once day 30 passes and destructive processing begins, the action is treated as irreversible and the member would need to register a new account to return to the service.
                </>,
            ],
        },
        ar: {
            heading: "٨. إلغاء الحذف المُجدوَل",
            body: [
                <>
                    يمكن لصاحب الحساب إلغاء الحذف المُجدوَل في أي وقتٍ خلال <strong>مهلة الإلغاء البالغة ٣٠ يومًا</strong>. ويُنفَّذ الإلغاء بتسجيل الدخول إلى تطبيق CrewZNatioN من جهازٍ موثوق وتأكيد أمر الإلغاء؛ فيُستعاد الحساب والمحتوى الذي أُخفي في اليوم صفر إلى الخدمة الحيّة.
                </>,
                <>
                    وإذا كان الحذف قد طُلب عبر البريد الإلكتروني، يمكن للعضو أيضًا الردّ على المحادثة نفسها مع الدعم خلال مهلة الثلاثين يومًا وطلب الإلغاء من <Brand />. وستتحقّق <Brand /> من الطلب بوسائل التحقّق ذاتها الموضّحة في الفقرة ٤ قبل استرجاع الحساب.
                </>,
                <>
                    وبمجرّد انقضاء اليوم الثلاثين وبدء عملية الحذف الفعلية، يُعدّ الإجراء نهائيًا، ويتعيّن على العضو إنشاء حسابٍ جديد للعودة إلى الخدمة.
                </>,
            ],
        },
    },
    {
        id: "support",
        en: {
            heading: "9. Support and contact",
            body: [
                <>
                    General questions about this process, or requests for help with the in-app flow, may be sent to <a className="text-[var(--crewz-gold)] hover:underline" href={`mailto:${PRIVACY_EMAIL}?subject=Account%20Deletion%20-%20Support`}>{PRIVACY_EMAIL}</a>, or submitted through the <Link to="/contact" className="text-[var(--crewz-gold)] hover:underline">Contact</Link> page. The full data-handling framework is described in the <Link to="/privacy" className="text-[var(--crewz-gold)] hover:underline">Privacy Policy</Link>.
                </>,
            ],
        },
        ar: {
            heading: "٩. الدعم والتواصل",
            body: [
                <>
                    يمكن إرسال الاستفسارات العامة حول هذه العملية، أو طلبات المساعدة في تنفيذها داخل التطبيق، إلى <a className="text-[var(--crewz-gold)] hover:underline" dir="ltr" href={`mailto:${PRIVACY_EMAIL}?subject=%D8%A7%D9%84%D8%AD%D8%B0%D9%81%20-%20%D8%AF%D8%B9%D9%85`}>{PRIVACY_EMAIL}</a>، أو من خلال صفحة <Link to="/ar/contact" className="text-[var(--crewz-gold)] hover:underline">التواصل</Link>. ويُطالَع الإطار الكامل لمعالجة البيانات في <Link to="/ar/privacy" className="text-[var(--crewz-gold)] hover:underline">سياسة الخصوصية</Link>.
                </>,
            ],
        },
    },
];

const COPY_EN = {
    overline: "Legal",
    title: <>Account Deletion</>,
    lead: <>How to permanently delete a <Brand /> account, what happens to associated data, and how to reach support if in-app deletion is not available.</>,
    effective: `Effective ${EFFECTIVE_EN}.`,
    controllerLine: <>Controller: {CONTROLLER_EN}. Operating from Riyadh, Kingdom of Saudi Arabia.</>,
    switchLabel: "العربية",
    metaTitle: "Account Deletion — CrewZNatioN",
    metaDesc:
        "Delete a CrewZNatioN account: in-app self-service instructions, written deletion request to support, identity verification, retention rules and deactivation alternative.",
    backHome: "Back to CrewZNatioN",
    canonical: "https://www.crewznation.com/account-deletion",
};

const COPY_AR = {
    overline: "قانوني",
    title: <>حذف الحساب</>,
    lead: <>كيفية حذف حساب <Brand /> بشكلٍ نهائي، وما يترتّب على ذلك من إجراءاتٍ على البيانات، وكيفية التواصل مع الدعم إذا لم يكن الحذف داخل التطبيق متاحًا.</>,
    effective: `تاريخ السريان ${EFFECTIVE_AR}.`,
    controllerLine: <>الجهة المتحكِّمة بالبيانات: {CONTROLLER_AR}. تعمل من مدينة الرياض، المملكة العربية السعودية.</>,
    switchLabel: "English",
    metaTitle: "حذف الحساب — CrewZNatioN",
    metaDesc:
        "حذف حساب CrewZNatioN: خطوات الخدمة الذاتية داخل التطبيق، والطلب الكتابي إلى الدعم، والتحقّق من الهوية، وقواعد الاحتفاظ، وبديل إلغاء التنشيط.",
    backHome: "العودة إلى CrewZNatioN",
    canonical: "https://www.crewznation.com/ar/account-deletion",
};

export default function AccountDeletion({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const Foot = isAr ? FooterAr : Footer;
    const t = isAr ? COPY_AR : COPY_EN;
    const other = isAr ? "/account-deletion" : "/ar/account-deletion";

    // Title, meta description, and canonical URL for App Store /
    // Play Console acceptance. Restores previous values on unmount
    // so navigating away doesn't leak a stale <title> or canonical.
    useEffect(() => {
        const prevTitle = document.title;
        document.title = t.metaTitle;

        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", t.metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", t.canonical);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [t.metaTitle, t.metaDesc, t.canonical]);

    return (
        <div
            className={`min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] ${isAr ? "font-arabic" : "font-body"}`}
            dir={isAr ? "rtl" : "ltr"}
            data-testid={isAr ? "account-deletion-page-ar" : "account-deletion-page"}
        >
            <Nav />

            {/* HEADER */}
            <section className="px-6 md:px-12 lg:px-20 pt-24 pb-10 max-w-4xl mx-auto">
                <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                    <span className="gold-rule" />
                    <p className={`overline ${isAr ? "font-arabic !text-[22.04px] md:!text-[23.8px]" : ""}`}>
                        {t.overline}
                    </p>
                </div>
                <h1
                    className={`font-display ${isAr ? "text-4xl md:text-5xl !leading-[1.25]" : "text-5xl md:text-6xl leading-[0.98]"} text-[var(--crewz-text)] max-w-3xl`}
                    data-testid="account-deletion-title"
                >
                    {t.title}
                </h1>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-8 max-w-2xl`}
                    data-testid="account-deletion-lead"
                >
                    {t.lead}
                </p>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-4 max-w-2xl`}
                    data-testid="account-deletion-effective"
                >
                    {t.effective}
                </p>
                <p
                    className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-sm leading-relaxed"} text-[var(--crewz-text)]/60 mt-1 max-w-2xl`}
                >
                    {t.controllerLine}
                </p>

                {/* Language switch */}
                <div className={`mt-8 ${isAr ? "text-right" : "text-left"}`}>
                    <Link
                        to={other}
                        dir={isAr ? "ltr" : "rtl"}
                        className="inline-flex items-center gap-2 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] px-5 py-2 text-[12px] uppercase tracking-[0.22em] font-bold transition-colors duration-300"
                        data-testid="account-deletion-lang-switch"
                        aria-label={isAr ? "Switch to English" : "التبديل إلى العربية"}
                    >
                        {t.switchLabel}
                    </Link>
                </div>
            </section>

            {/* BODY */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-16 max-w-4xl mx-auto"
                data-testid="account-deletion-body"
            >
                <div className="space-y-12">
                    {SECTIONS.map((s) => {
                        const c = isAr ? s.ar : s.en;
                        return (
                            <section key={s.id} id={s.id} data-testid={`account-deletion-section-${s.id}`}>
                                <h2
                                    className={`${isAr ? "font-arabic text-2xl md:text-3xl leading-[1.4]" : "font-display text-2xl md:text-3xl leading-[1.15]"} text-[var(--crewz-text)] mb-5 font-bold`}
                                >
                                    {c.heading}
                                </h2>
                                {c.body?.map((p, i) => (
                                    <p
                                        key={i}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mb-4 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                                {c.list && (
                                    <ul
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95] pr-5 space-y-2 [list-style:disc]" : "font-body text-base md:text-lg leading-relaxed font-light pl-5 space-y-2 list-disc"} text-[var(--crewz-text)]/85 mt-2 mb-4 marker:text-[var(--crewz-gold)] break-words`}
                                    >
                                        {c.list.map((li, i) => (
                                            <li key={i}>{li}</li>
                                        ))}
                                    </ul>
                                )}
                                {c.footer?.map((p, i) => (
                                    <p
                                        key={`f${i}`}
                                        className={`${isAr ? "font-arabic text-[17px] md:text-[19px] leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-2 break-words`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </section>
                        );
                    })}
                </div>

                {/* Cross-links */}
                <div className={`mt-16 flex flex-wrap gap-x-6 gap-y-2 ${isAr ? "justify-end" : ""}`}>
                    <Link
                        to={isAr ? "/ar/privacy" : "/privacy"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="account-deletion-privacy-link"
                    >
                        {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
                    </Link>
                    <Link
                        to={isAr ? "/ar/contact" : "/contact"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="account-deletion-contact-link"
                    >
                        {isAr ? "التواصل مع الدعم" : "Contact Support"}
                    </Link>
                    <Link
                        to={isAr ? "/ar" : "/"}
                        className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                        data-testid="account-deletion-back-home"
                    >
                        ← {t.backHome}
                    </Link>
                </div>
            </section>

            <Foot />
        </div>
    );
}
